/**
 * LoadingOverlay
 *
 * Full-screen overlay loader for long operations: the Nord Living wordmark
 * gently pulsing (static under reduced motion), page scroll locked.
 */

import { useEffect } from 'react';
import { BrandLogo } from '@/components/brand';
import { useMemeLoaderForUi } from '@/providers/MemeLoaderPreferenceProvider';

import { LoadingOverlayBody } from './LoadingOverlayBody';
import { MemeMediaView, MEME_GIF_URLS, useRandomMemeMediaIndex } from './meme-loader';

interface LoadingOverlayProps {
  open: boolean;
  title?: string;
  description?: string;
  className?: string;
}

function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}

function overlayMainVisual(memeLoaderEnabled: boolean, mediaIndex: number) {
  if (memeLoaderEnabled) {
    return <MemeMediaView src={MEME_GIF_URLS[mediaIndex]} layout="overlay" />;
  }
  return (
    <div className="flex h-40 w-72 items-center justify-center">
      <BrandLogo decorative className="h-14 w-full motion-safe:animate-pulse" />
    </div>
  );
}

export function LoadingOverlay({ open, title = 'Loading…', description, className }: LoadingOverlayProps) {
  useBodyScrollLock(open);
  const { memeLoaderEnabled } = useMemeLoaderForUi();
  const mediaIndex = useRandomMemeMediaIndex(MEME_GIF_URLS.length);

  if (!open) return null;

  return (
    <LoadingOverlayBody
      title={title}
      description={description}
      className={className}
      lottieSlot={overlayMainVisual(memeLoaderEnabled, mediaIndex)}
    />
  );
}
